// #21 — read-only "ACE Routing Rules" page, shown only on the Applied GA
// (top-level) admin dashboard. Reflects the hardcoded rules served by
// GET /api/ace-routing, so the screen always matches what the router actually
// does. Phase 1: read-only (rules live in code; not yet admin-editable).
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Title } from "react-admin";
import aceRoutingService from "../../../services/aceRouting.service";

// A small, muted category label for each rule's routing dimension — quieter than
// a colored pill so the rule text stays the focus.
const DimensionLabel = ({ children }) => (
  <Typography
    component="span"
    sx={{
      minWidth: 76,
      flexShrink: 0,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontSize: "0.7rem",
      fontWeight: 700,
      mt: "2px",
    }}
  >
    {children}
  </Typography>
);

const AceRouting = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    aceRoutingService
      .list()
      .then(setData)
      .catch(() => setError("Could not load the ACE routing rules."));
  }, []);

  return (
    <Box sx={{ maxWidth: 900, mt: 2 }}>
      <Title title="ACE Routing Rules" />
      <Typography variant="h5" gutterBottom>
        ACE Routing Rules
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        How a new contract request is routed to an Applied GA ACE. Rules are
        evaluated most-specific first — agency, then FSD (Field Sales Director),
        then territory — and the first match wins. Anything that matches no rule
        falls back to {data?.fallback || "Isela"}. These rules are hardcoded
        (read-only) for now.
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {data && (
        <>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>ACE</strong></TableCell>
                  <TableCell><strong>Routes here when…</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.aces.map(({ ace, rules }) => (
                  <TableRow key={ace} hover>
                    <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {ace}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.75}>
                        {rules.map((rule, i) => (
                          <Box
                            key={i}
                            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                          >
                            <DimensionLabel>{rule.dimension}</DimensionLabel>
                            <Box>
                              <Typography variant="body2">{rule.summary}</Typography>
                              {rule.note && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  {rule.note}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow hover>
                  <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                    {data.fallback}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <DimensionLabel>Fallback</DimensionLabel>
                      <Typography variant="body2">
                        Any request that matches no rule above.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AceRouting;
